/**
 * AgentBroker REST API client — общий для всех React-виджетов.
 *
 * Модуль v1 подключён глобально (common/config/modules.php) → same-origin
 * на основном домене. CORS не нужен, CSRF нужен только для mutations
 * (POST/PUT/DELETE) — добавим когда появятся.
 */

import { getGuestKey } from './guestKey';

export class ApiError extends Error {
    status: number;
    body?: unknown;
    constructor(status: number, body?: unknown, message?: string) {
        super(message ?? `API ${status}`);
        this.status = status;
        this.body = body;
    }
}

/**
 * GET/POST запрос к API, возвращает распарсенный JSON.
 *
 * - `credentials: 'same-origin'` — отправляет сессионную cookie агента
 * - 401 → редирект на /user/login (сохраняет текущий URL через returnUrl)
 * - не-JSON ответ → ApiError с body как строкой
 */
export async function apiFetch<T = unknown>(
    path: string,
    init: RequestInit = {},
): Promise<T> {
    const response = await fetch(path, {
        credentials: 'same-origin',
        // ВАЖЕН порядок: `...init` идёт ПЕРВЫМ, а `headers` — последним. Иначе
        // `...init` (несущий собственный `headers`, напр. {'Content-Type':...} у
        // POST-вызовов вроде prepare-offer) затирал бы смерженный объект ниже и
        // выкидывал из запроса `Guest-Key`/`Accept` → гость получал 401 и улетал
        // на /user/login (баг «для незарегистрированных ипотека не работает»).
        ...init,
        headers: {
            Accept: 'application/json',
            // Гость без сессии агента идентифицируется Guest-Key (см. _shared/guestKey).
            'Guest-Key': getGuestKey(),
            ...(init.headers ?? {}),
        },
    });

    if (response.status === 401) {
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/user/login?return=${returnUrl}`;
        throw new ApiError(401, null, 'Unauthorized');
    }

    const contentType = response.headers.get('content-type') ?? '';
    const isJson = contentType.includes('application/json');
    const body = isJson ? await response.json().catch(() => null) : await response.text();

    if (!response.ok) {
        const msg = isJson && body && typeof body === 'object' && 'message' in body
            ? String((body as { message: unknown }).message)
            : `API ${response.status}`;
        throw new ApiError(response.status, body, msg);
    }

    // Бэк отдаёт ошибки конвертом `{status:'error'|'forbidden', message}` —
    // иногда даже с HTTP 200 (легаси-экшены). Не пропускаем такой объект в
    // вызывающий код: иначе он попадёт туда, где ждут массив/данные, и виджет
    // упадёт с невнятным `.some is not a function` вместо показа ошибки.
    if (
        isJson && body && typeof body === 'object' &&
        'status' in body &&
        ((body as { status?: unknown }).status === 'error' ||
            (body as { status?: unknown }).status === 'forbidden')
    ) {
        const msg = 'message' in body
            ? String((body as { message: unknown }).message)
            : `API ${response.status}`;
        throw new ApiError(response.status, body, msg);
    }

    return body as T;
}
